import { type ColumnDef } from '@tanstack/react-table'
import {
  ClipboardCheck,
  Edit2,
  MapPin,
  Mic2,
  MoreHorizontal,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { TableMasterResponse } from '@/lib/api/client/types.gen'

export type TableData = TableMasterResponse & {
  occupancy?: number
}

export const createColumns = (
  onEdit: (table: TableData) => void,
  onDelete: (id: string) => void,
  onAddGuest: (table: TableData) => void,
): ColumnDef<TableData>[] => [
  {
    accessorKey: 'tableNumber',
    header: 'โต๊ะ / ประเภท',
    cell: ({ row }) => {
      const table = row.original
      const type = table.type || 'table'
      const isTable = type === 'table'

      const getTypeConfig = () => {
        switch (type) {
          case 'stage':
            return {
              icon: <Mic2 className="size-4" />,
              bgColor: 'bg-orange-50',
              textColor: 'text-orange-500',
              label: table.tableNumber || 'Stage',
            }
          case 'registration':
            return {
              icon: <ClipboardCheck className="size-4" />,
              bgColor: 'bg-blue-50',
              textColor: 'text-blue-500',
              label: table.tableNumber || 'Registration',
            }
          case 'other':
            return {
              icon: <MapPin className="size-4" />,
              bgColor: 'bg-gray-50',
              textColor: 'text-gray-500',
              label: table.tableNumber || 'Other',
            }
          default:
            return {
              icon: null,
              bgColor: 'bg-primary/5',
              textColor: 'text-primary',
              label: table.tableNumber.startsWith('โต๊ะ')
                ? table.tableNumber
                : `โต๊ะ ${table.tableNumber}`,
            }
        }
      }

      const config = getTypeConfig()

      return (
        <div className="flex items-center gap-3">
          <div
            className={`flex size-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold shadow-xs ring-1 ring-black/3 ${config.bgColor} ${config.textColor}`}
          >
            {isTable ? table.tableNumber : config.icon}
          </div>
          <span className="font-bold text-gray-900">{config.label}</span>
        </div>
      )
    },
  },
  {
    accessorKey: 'capacity',
    header: 'ที่นั่ง',
    cell: ({ row }) => {
      const table = row.original
      if (table.type !== 'table' && table.type !== undefined) return '-'

      const capacity = table.capacity || 10
      const occupancy = table.occupancy || 0
      const isFull = occupancy >= capacity

      return (
        <div
          className={`flex items-center gap-2 font-semibold ${
            isFull ? 'text-destructive' : 'text-gray-600'
          }`}
        >
          <Users className="size-4" />
          <span>
            {occupancy} / {capacity}
          </span>
        </div>
      )
    },
  },
  {
    accessorKey: 'notes',
    header: 'หมายเหตุ',
    cell: ({ row }) => {
      const notes = row.original.notes
      return notes ? (
        <span className="text-muted-foreground line-clamp-1 max-w-50 text-sm italic">
          {notes}
        </span>
      ) : (
        <span className="text-gray-300">-</span>
      )
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const table = row.original

      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  className="size-8 p-0"
                />
              }
            >
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuLabel>การจัดการ</DropdownMenuLabel>
                {table.type === 'table' || table.type === undefined ? (
                  <DropdownMenuItem onClick={() => onAddGuest(table)}>
                    <UserPlus className="mr-2 size-4" />
                    เพิ่มแขกเข้าโต๊ะ
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={() => onEdit(table)}>
                  <Edit2 className="mr-2 size-4" />
                  แก้ไขข้อมูล
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => onDelete(table.id)}
              >
                <Trash2 className="mr-2 size-4" />
                ลบโต๊ะ
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    },
  },
]
